import { useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Global page-refresh control — appears in the shared header so every
 * route in the app exposes a consistent "refresh this page" affordance.
 *
 * Behaviour:
 *  • Preserves the current URL/route (no navigation).
 *  • Preserves auth session (cookies survive `location.reload`).
 *  • Shows a spinning icon while the reload is in flight.
 *  • Prevents repeated clicks while a refresh is already running.
 *  • Does NOT trigger source ingestion, AI analysis, or publishing —
 *    it only re-loads the current page's data.
 *
 * Kept intentionally tiny and reusable across the public shell
 * (`components/homepage/Header.jsx`) and the admin shell
 * (`pages/admin/AdminLayout.jsx`).
 */
export const RefreshButton = ({
  className = "",
  size = "sm",       // "sm" (admin) | "md" (public)
  testId = "global-refresh-button",
}) => {
  const [busy, setBusy] = useState(false);

  const onClick = useCallback(() => {
    if (busy) return;
    setBusy(true);
    // Short paint frame so the spinner is visible before the reload.
    setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        setBusy(false);
      }
    }, 60);
  }, [busy]);

  const dim = size === "md" ? "h-10 w-10" : "h-9 w-9";
  const icon = size === "md" ? "h-4 w-4" : "h-4 w-4";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy}
      aria-label="تحديث الصفحة"
      title="تحديث الصفحة"
      data-testid={testId}
      className={`inline-flex ${dim} shrink-0 items-center justify-center rounded-lg text-ink-500 transition-colors hover:bg-surface-100 hover:text-ink-900 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
      <RefreshCw className={`${icon} ${busy ? "animate-spin" : ""}`} strokeWidth={1.75} />
    </button>
  );
};

export default RefreshButton;
